import React, {useEffect, useRef, useState} from 'react';
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {Checkbox} from 'primereact/checkbox';
import {Messages} from "primereact/messages";

import {UnAuthService} from "../../service/UnAuthService";
import {LanguageService} from "../../service/LanguageService";

import {Password} from "primereact/password";

import {emailValidator, formValid, getKeyValue} from "../../utils/Utils";
import {AppContext} from "../../HomePage";
import {Dialog} from "primereact/dialog";
import SocialLoginButtons from "./SocialLoginButtons";
import AuthLayout from "./AuthLayout";
import AuthField from "./AuthField";
import AuthCustomerStrip from "./AuthCustomerStrip";
import {withOpenAIAttribution} from "../../utils/openaiAttribution";


const Register = (props) =>  {

    const {t, navigate, setError} = React.useContext(AppContext);
    const messages = useRef(null);
    const agreeRef = useRef(null);

    const [user, setUser] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        agree: false,
        language: LanguageService.getLanguage(),
        // The backend derives country and currency from `language`, and the
        // timezone is read from the browser rather than hardcoded to Istanbul.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
    });

    const [formErrors, setFormErrors] = useState({
        first_name: '', last_name: '', email: '', password: '', agree: ''
    });

    const [formError, setFormError] = useState('');
    const [showTerms, setShowTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const googleAnalyticsId = 'G-PYCPB75WSR';
    const googleAdsId = 'AW-370624605';
    const conversionId = 'AW-370624605/WdglCN2DnrMcEN2Q3bAB';

    React.useEffect(() => {
        window.dataLayer = window.dataLayer || [];
        window.gtag = window.gtag || function () {
            window.dataLayer.push(arguments);
        };

        if (!document.querySelector('script[src^="https://www.googletagmanager.com/gtag/js"]')) {
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
            document.head.appendChild(script);

            window.gtag('js', new Date());
        }

        window.gtag('config', googleAdsId);
        window.gtag('config', googleAnalyticsId, {
            send_page_view: false
        });
        window.gtag('event', 'page_view', {
            send_to: googleAnalyticsId,
            page_title: 'Register',
            // HashRouter's /#/register URL would otherwise be reported as /.
            page_location: `${window.location.origin}/register`
        });
    }, []);

    // Fired only after the account is actually created, so the conversion
    // count reflects registrations rather than visits to this page.
    const trackRegistrationConversion = () => {
        if (window.gtag)
            window.gtag('event', 'conversion', {'send_to': conversionId});
    };

    // PrimeReact 6's Checkbox doesn't forward arbitrary aria-* props to its
    // native input the way InputText/Password do, so the terms error is
    // wired to it imperatively instead of via AuthField's cloneElement.
    useEffect(() => {
        if (!agreeRef.current)
            return;

        if (formErrors.agree)
            agreeRef.current.setAttribute('aria-describedby', 'agree-error');
        else
            agreeRef.current.removeAttribute('aria-describedby');
    }, [formErrors.agree]);

    const register = (event) => {

        event.preventDefault();

        if (loading)
            return;

        setFormError('');
        setSubmitted(true);

        setLoading(true);

        checkErrors(user).then(formErrors=> {
            if (formValid(formErrors)) {
                // Read the language at submit time rather than trusting the
                // mount-time snapshot on `user.language`: AuthLayout's header
                // switcher can change the active language in place, without a
                // reload, after this component first rendered.
                const payload = {...user, email: user.email.trim(), language: LanguageService.getLanguage()};

                // The OpenAI ad reference rides along; the backend reports the
                // conversion itself once the account exists.
                UnAuthService.register(withOpenAIAttribution(payload)).then( data => {
                    trackRegistrationConversion();
                    if (data.is_approved)
                        UnAuthService.login(payload.email, user.password).then(() => {
                            // The demo campaign is cloned server-side on first
                            // login, so we no longer create an empty "Trial
                            // Campaign" here — just land the user on it.
                            navigate("/product");
                        }).catch(error =>{
                            setError(error);
                            setError(null);
                            setLoading(false);
                        });
                    else
                        navigate("/login?register=1");
                }).catch(error => {
                    const data = error.response && error.response.data;
                    const translated = data && data.error_key ? t(data.error_key) : null;

                    if (translated && translated !== data.error_key)
                        setFormError(translated);
                    else {
                        setError(error);
                        setError(null);
                    }

                    setLoading(false);
                });
            }
            else
            {
                setFormErrors(formErrors);
                setLoading(false);
            }
        });
    }

    const handleSocialLoginSuccess = () => {
        navigate("/campaign");
    };

    const login = () => {
        navigate("/login");
    };

    const onChange = (e, a) => {

        const { key, value } = getKeyValue(e);

        setUser({...user, [key] : value});

        if (submitted)
            setFormErrors(prev => ({...prev, [key]: checkError(key, value)}));
    };

    const checkError = (key, value) => {

        if (key === "first_name")
            return value.trim() === "" ? t('login.first_name_required') : "";
        if (key === "last_name")
            return value.trim() === "" ? t('login.last_name_required') : "";
        if (key === "email") {
            const trimmed = value.trim();
            return trimmed === "" ? t('login.email_required') : emailValidator(trimmed, t);
        }
        if (key === "password")
            return value === "" ? t('login.password_required') : "";
        if (key === "agree")
            return !value ? t('login.terms_required') : "";

        return "";
    };

    const checkErrors = async (user) => {

        let errors = { ...formErrors };

        Object.entries(user).forEach(([key, value]) => {
            errors[key] = checkError(key, value);
        });

        return errors;
    };

    return <AuthLayout title={t('login.register_title')} subtitle={t('login.register_subtitle')}
                       footer={<span>{t('login.already_have_account')}
                           <button type="button" className="auth-ux__link" onClick={login}>{t('actions.login')}</button>
                       </span>}>

        <Messages ref={messages}/>

        {/* The marketing site never states the trial terms, so this is the
            first place the visitor reads them. Keeping them above the form
            answers "will they ask for my card?" before the first keystroke. */}
        <div className="auth-ux__offer">
            <span className="auth-ux__offer-icon"><i className="pi pi-check"/></span>
            <div className="auth-ux__offer-text">
                <strong className="auth-ux__offer-title">{t('login.trial_length')}</strong>
                <span className="auth-ux__offer-meta">
                    {t('login.trial_no_card')} &middot; {t('login.trial_cancel')}
                </span>
            </div>
        </div>

        <form onSubmit={register} noValidate>

            {formError && <div className="auth-ux__form-error" role="alert">{formError}</div>}

            <AuthField id="first_name" label={t('user.first_name')} error={formErrors.first_name}>
                <InputText name="first_name" autoComplete="given-name" value={user.first_name} onChange={onChange}/>
            </AuthField>

            <AuthField id="last_name" label={t('user.last_name')} error={formErrors.last_name}>
                <InputText name="last_name" autoComplete="family-name" value={user.last_name} onChange={onChange}/>
            </AuthField>

            <AuthField id="email" label={t('user.email')} error={formErrors.email}>
                <InputText name="email" type="email" inputMode="email" autoComplete="username"
                           value={user.email} onChange={onChange}/>
            </AuthField>

            <AuthField id="password" label={t('user.password')} error={formErrors.password}>
                <Password name="password" feedback={true} toggleMask={true} autoComplete="new-password"
                          value={user.password} onChange={onChange}/>
            </AuthField>

            <div className="auth-ux__field">
                <label className="auth-ux__terms">
                    <Checkbox inputId="agree" name="agree" value={user.agree} checked={user.agree} onChange={onChange} inputRef={agreeRef}/>
                    <span>{t('login.terms_agree_label')}</span>
                </label>
                <button type="button" className="auth-ux__link" onClick={() => setShowTerms(true)}>
                    {t('user.terms_conditions')}
                </button>
                {formErrors.agree && <small className="auth-ux__error" id="agree-error" role="alert">{formErrors.agree}</small>}
            </div>

            <Button type="submit" label={t('actions.sign_up')} icon="pi pi-user-plus"
                    className="auth-ux__submit" disabled={loading} loading={loading}/>
        </form>

        <div className="auth-ux__divider"><span>{t('login.social_separator')}</span></div>

        <SocialLoginButtons t={t} messages={messages} setError={setError} mode="signup"
                            onSuccess={handleSocialLoginSuccess} disabled={loading}/>

        {/* On phones the brand column collapses to a bare logo, so the client
            logos it carries never reach the visitor. This is the same strip in
            its light variant; CSS shows exactly one of the two. */}
        <AuthCustomerStrip variant="inline"/>

        {showTerms &&
        <Dialog visible={true} style={{width: '50vw', minHeight: '50vh', overflow: "auto"}}
                maximizable={true} modal={true} onHide={() => setShowTerms(false)}>
            <iframe title="terms" width="100%" height="800px"
                    src={LanguageService.getLanguage().toLowerCase() === "tr" ?
                        "https://www.pricerest.com/tr/user-agreement.pdf" :
                        "https://www.pricerest.com/user-agreement.pdf"}/>
        </Dialog>}
    </AuthLayout>;

};
export default Register;
